import { useEffect, useMemo, useState } from "react";
import { useSDK } from "../lib/sdk-context";
import { api } from "../lib/api";
import type { BucketRow } from "../components/BucketList";
import ReviewCard from "../components/ReviewCard";
import ChainReviewCard, { type ChainMatchPayload } from "../components/ChainReviewCard";
import DeliveryPartnerModal from "../components/DeliveryPartnerModal";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";

type BucketKey = "review" | "unmatched" | "auto" | "user" | "other";

function classify(r: BucketRow): BucketKey {
  if (r.link_status === "confirmed") return r.user_confirmed ? "user" : "auto";
  if (r.link_status === "suggested" && r.license_number) return "review";
  if (r.link_status === "suggested" && r.chain_payload) return "review";
  if (r.link_status === "suggested" && !r.license_number) return "unmatched";
  return "other";
}

export default function Review() {
  const { context } = useSDK();
  const toast = useToast();
  const [rows, setRows] = useState<BucketRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeBucket, setActiveBucket] = useState<BucketKey>("review");
  const [dpOpen, setDpOpen] = useState(false);
  const [dpRow, setDpRow] = useState<BucketRow | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(context, "/api/match/buckets?type=cm");
      setRows(r.rows || []);
    } catch (err) {
      toast.push(`Failed to load: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [context]);

  const counts = useMemo(() => {
    const out = { review: 0, unmatched: 0, auto: 0, user: 0, other: 0, total: rows.length };
    for (const r of rows) out[classify(r)]++;
    return out;
  }, [rows]);

  const filtered = useMemo(() => rows.filter((r) => classify(r) === activeBucket), [rows, activeBucket]);

  const reviewedCount = counts.auto + counts.user;
  const pctReviewed = counts.total > 0 ? Math.round((reviewedCount / counts.total) * 100) : 0;

  const onConfirm = async (row: BucketRow, payload: any) => {
    try {
      await api.patch(context, `/api/match/link/${encodeURIComponent(row.source_row_id)}`, payload);
      await load();
    } catch (err) {
      toast.push(`Action failed: ${(err as Error).message}`, "error");
    }
  };
  const onNotARetailer = async (row: BucketRow) => {
    const priorStatus = row.link_status;
    const priorLicense = row.license_number;
    const priorMethod = row.link_method;
    try {
      await api.patch(context, `/api/match/link/${encodeURIComponent(row.source_row_id)}`, {
        link_status: "not_a_retailer",
        license_number: null,
        link_method: "not_a_retailer",
        notes: "marked not a retailer by user",
      });
      await load();
      const revertStatus = priorStatus === "confirmed" ? "confirmed" : "suggested";
      toast.push("Marked as not a retailer.", {
        tone: "success",
        durationMs: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.patch(context, `/api/match/link/${encodeURIComponent(row.source_row_id)}`, {
                link_status: revertStatus,
                license_number: priorLicense,
                link_method: priorMethod ?? "manual",
                notes: "undo mark_not_a_retailer",
              });
              await load();
              toast.push("Reverted.", "info");
            } catch (err) {
              toast.push(`Undo failed: ${(err as Error).message}`, "error");
            }
          },
        },
      });
    } catch (err) {
      toast.push(`Action failed: ${(err as Error).message}`, "error");
    }
  };
  const onEnrichStart = (license: string) => {
    window.location.hash = `#/review/enrich/${encodeURIComponent(license)}`;
  };
  const openDp = (row: BucketRow) => {
    setDpRow(row);
    setDpOpen(true);
  };

  const onLinkChain = async (row: BucketRow, chainId: string, chainDisplayName: string) => {
    if (!row.license_number) {
      toast.push("Pick a retailer first — chain linking needs a license number.", "error");
      throw new Error("no_license_number");
    }
    try {
      await api.post(context, "/api/admin/chains/link-license", {
        license_number: row.license_number,
        chain_id: chainId,
      });
      await load();
      toast.push(`Linked ${row.license_number} to ${chainDisplayName}.`, "success");
    } catch (err) {
      toast.push(`Link failed: ${(err as Error).message}`, "error");
      throw err;
    }
  };

  const onConfirmChain = async (
    row: BucketRow,
    payload: ChainMatchPayload,
    selected: string[]
  ): Promise<{ chain_action_id: string; confirmed_count: number }> => {
    try {
      const resp = await api.post(context, "/api/match/confirm-chain", {
        chain_id: payload.chain_id,
        source_row_id: row.source_row_id,
        selected_license_numbers: selected,
      });
      const chainActionId = resp?.chain_action_id as string;
      const count = resp?.confirmed_count as number;
      await load();
      toast.push(
        `Confirmed ${count} member${count === 1 ? "" : "s"} of ${payload.display_name}.`,
        {
          tone: "success",
          durationMs: 10000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                await api.post(context, "/api/match/undo-chain", { chain_action_id: chainActionId });
                await load();
                toast.push("Reverted.", "info");
              } catch (err) {
                toast.push(`Undo failed: ${(err as Error).message}`, "error");
              }
            },
          },
        }
      );
      return { chain_action_id: chainActionId, confirmed_count: count };
    } catch (err) {
      toast.push(`Action failed: ${(err as Error).message}`, "error");
      throw err;
    }
  };

  const onPickDifferentForChain = async (row: BucketRow, lic: string) => {
    try {
      await api.patch(context, `/api/match/link/${encodeURIComponent(row.source_row_id)}`, {
        link_status: "confirmed",
        license_number: lic,
        link_method: "manual",
        notes: "manually picked from chain card",
      });
      await load();
    } catch (err) {
      toast.push(`Action failed: ${(err as Error).message}`, "error");
    }
  };

  const onSkipChain = async (row: BucketRow) => {
    try {
      await api.patch(context, `/api/match/link/${encodeURIComponent(row.source_row_id)}`, {
        link_status: "skipped",
        link_method: "manual",
        notes: "skipped chain card",
      });
      await load();
    } catch (err) {
      toast.push(`Action failed: ${(err as Error).message}`, "error");
    }
  };

  const pill = (key: BucketKey, label: string, count: number, tone: "auto" | "user" | "review" | "unmatched") => {
    const active = activeBucket === key;
    const toneClasses = {
      auto: active ? "bg-[#F1F8F2] text-[#1C5125] border-[#BFE0C4]" : "bg-white text-[#1C5125] border-[#BFE0C4]/60 hover:bg-[#F1F8F2]/60",
      user: active ? "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]" : "bg-white text-[#065F46] border-[#A7F3D0]/60 hover:bg-[#ECFDF5]/60",
      review: active ? "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]" : "bg-white text-[#9A3412] border-[#FED7AA]/60 hover:bg-[#FFF7ED]/60",
      unmatched: active ? "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]" : "bg-white text-[#991B1B] border-[#FECACA]/60 hover:bg-[#FEF2F2]/60",
    }[tone];
    return (
      <button
        key={key}
        onClick={() => setActiveBucket(key)}
        className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${toneClasses} ${active ? "ring-1 ring-inset ring-current/20" : ""}`}
      >
        {label} <span className="font-semibold">{count.toLocaleString()}</span>
      </button>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Review queue</h2>
          <p className="text-sm text-gray-600 mt-1">
            {loading ? "Loading…" : `${counts.total.toLocaleString()} Customer Master rows`}
          </p>
          {!loading && counts.total > 0 && (
            <p className="text-xs text-gray-500 mt-0.5">
              Reviewed: <span className="font-medium text-gray-700">{reviewedCount.toLocaleString()}</span>
              {" · "}
              Pending: <span className="font-medium text-gray-700">{(counts.review + counts.unmatched).toLocaleString()}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => api.exportCsv(context)}>Export CSV</Button>
          <Button variant="ghost" onClick={() => (window.location.hash = "#/upload/cm")}>New upload</Button>
        </div>
      </div>

      {!loading && counts.total > 0 && (
        <div className="mb-4">
          <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full bg-accent-500 transition-all" style={{ width: `${pctReviewed}%` }} />
          </div>
          <p className="mt-1 text-xs text-gray-500">{pctReviewed}% reviewed</p>
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-2">
        {pill("auto", "AUTO_APPROVED", counts.auto, "auto")}
        {pill("user", "USER_MATCHED", counts.user, "user")}
        {pill("review", "NEEDS_REVIEW", counts.review, "review")}
        {pill("unmatched", "UNMATCHED", counts.unmatched, "unmatched")}
        {counts.other > 0 && (
          <button
            onClick={() => setActiveBucket("other")}
            className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium ${activeBucket === "other" ? "bg-gray-100 text-gray-800 border-gray-300" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
          >
            Other <span className="font-semibold">{counts.other}</span>
          </button>
        )}
      </div>

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-gray-500 text-sm">Loading review queue…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center">
          <p className="text-gray-700 text-sm font-medium">Nothing here.</p>
          <p className="text-gray-500 text-xs mt-1">No rows in this bucket.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((r) => {
            if (r.chain_payload && r.link_status !== "confirmed") {
              return (
                <ChainReviewCard
                  key={r.source_row_id}
                  row={r}
                  payload={r.chain_payload}
                  onConfirmChain={onConfirmChain}
                  onPickDifferent={onPickDifferentForChain}
                  onMarkDeliveryPartner={openDp}
                  onNotARetailer={onNotARetailer}
                  onSkip={onSkipChain}
                />
              );
            }
            return (
              <ReviewCard
                key={r.source_row_id}
                row={r}
                onConfirm={onConfirm}
                onNotARetailer={onNotARetailer}
                onEnrichStart={onEnrichStart}
                onMarkDeliveryPartner={openDp}
                onLinkChain={onLinkChain}
              />
            );
          })}
        </div>
      )}

      <DeliveryPartnerModal
        row={dpRow}
        open={dpOpen}
        onOpenChange={setDpOpen}
        onSaved={load}
      />
    </div>
  );
}
